import { Link } from "react-router-dom";
import { ArrowLeft, FileText } from "@phosphor-icons/react";
import Logo from "@/components/Logo";

const LAST_UPDATED = "February 2026";

export default function Terms() {
  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans" data-testid="terms-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-black/10">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-sm text-neutral-700 hover:text-black" data-testid="terms-back-home">
              <ArrowLeft size={16} weight="bold" />
              <span className="hidden sm:inline">Home</span>
            </Link>
            <div className="h-4 w-px bg-black/20" />
            <Logo />
          </div>
          <div className="label-mono">Terms</div>
        </div>
      </header>

      {/* Body */}
      <article className="max-w-[900px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="label-mono mb-6">
          <span className="inline-block w-6 h-px bg-black/40 align-middle mr-2" />
          Legal / Terms of Service
        </div>
        <h1 className="font-heading font-black tracking-tighter leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
          Terms &amp; Conditions
        </h1>
        <div className="mt-4 text-sm text-neutral-500 font-mono uppercase tracking-widest">Last updated · {LAST_UPDATED}</div>

        <div className="mt-6 border border-black/10 p-5 flex items-start gap-3 bg-neutral-50">
          <FileText size={20} weight="duotone" className="text-[#2400FF] shrink-0 mt-0.5" />
          <p className="text-sm text-neutral-700 leading-relaxed">
            <b>Short version:</b> MyResumeDesign is provided as a self-service tool. You own the content you
            create. AI-generated rewrites are suggestions — you are responsible for reviewing them for
            accuracy. There is no account, no data retention, and the tool is offered "as is".
          </p>
        </div>

        <div className="prose-legal mt-10 space-y-8 text-neutral-800 leading-relaxed">

          <Section n="1" title="Acceptance">
            <p>
              By accessing or using MyResumeDesign (the "Service"), you agree to be bound by these Terms &amp;
              Conditions ("Terms"). If you do not agree, do not use the Service.
            </p>
          </Section>

          <Section n="2" title="Description of the Service">
            <p>
              MyResumeDesign is a browser-based résumé builder. It lets you enter your career information,
              choose from multiple templates, optionally polish text with an AI language model, tailor content
              to a job description, and export the result as a PDF via your browser's native print pipeline.
              No user account is created and no résumé data is stored on our servers.
            </p>
          </Section>

          <Section n="3" title="No Account, No Retention">
            <p>
              The Service does not require registration. All content you enter or upload lives in your active
              browser session and is discarded when you close the tab or navigate away. Downloading the PDF is
              the only way to persist your work. You are solely responsible for saving your résumé.
            </p>
          </Section>

          <Section n="4" title="Acceptable Use">
            <p>
              You agree to use the Service only for lawful purposes and only to create a résumé, cover letter
              or similar personal document. You will not:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>submit content that is unlawful, defamatory, infringing, or that impersonates another person;</li>
              <li>use the AI features to generate false credentials, misleading employment history, or fraudulent claims;</li>
              <li>attempt to reverse-engineer, decompile, scrape, overload or otherwise disrupt the Service;</li>
              <li>use automated scripts or bots against the tool beyond ordinary personal use;</li>
              <li>upload files containing malware, executable code or content that violates the rights of others.</li>
            </ul>
          </Section>

          <Section n="5" title="AI-Generated Content">
            <p>
              The AI polish and Job-Description-match features use a third-party large language model to rewrite
              or analyze the text you provide. AI features are currently powered by Anthropic Claude, routed
              through Emergent's inference layer.
            </p>
            <p>
              AI output is a suggestion, not a fact. It may contain errors, omissions, or subtly changed
              meaning. <b>You are responsible for reviewing every AI-produced line before using it in a job
              application.</b> We make no warranty that AI output is accurate, unique, or free of bias.
            </p>
          </Section>

          <Section n="6" title="Résumé Import (File Upload)">
            <p>
              The Import feature accepts .pdf and .docx files. Uploaded files are transmitted for the sole
              purpose of extracting text into the builder form and are not retained after parsing. You must
              have the right to upload the file and to have its content processed.
            </p>
          </Section>

          <Section n="7" title="Payments &amp; Download Access">
            <p>
              MyResumeDesign is a paid service: to download your résumé as a PDF you must first complete a
              one-time payment via Razorpay. The paywall is not optional — there is no free "watermarked"
              download path. All payment card and UPI details are entered through Razorpay's secure widget;
              we do not see or store them.
            </p>
            <p>
              A successful payment entitles you to download the résumé generated in the current browser
              session <b>up to three (3) times</b>. This limit is enforced client-side per browser session;
              closing the tab or clearing session storage resets the counter and would require a new payment
              to download again.
            </p>
            <p>
              Because the deliverable is a digital PDF you download immediately, payments are generally
              non-refundable once the first successful download has been completed. Refund requests due to a
              technical failure (e.g., the download never started, all three attempts errored out before a
              PDF was saved) may be considered on a case-by-case basis via
              {" "}<a href="mailto:contact@myresumedesign.com" className="underline hover:text-black">contact@myresumedesign.com</a>.
            </p>
          </Section>

          <Section n="8" title="Intellectual Property">
            <p>
              You retain all rights to the résumé content you write and to the personal information you
              provide. We claim no ownership of your content. The Service itself — its code, templates,
              type system, iconography and design — remains the property of MyResumeDesign and its licensors,
              protected by copyright and other intellectual property laws.
            </p>
            <p>
              The templates are provided for use in personal, non-commercial job applications. You may not
              redistribute or resell the templates as a standalone product.
            </p>
          </Section>

          <Section n="9" title="Protection Layer">
            <p>
              The Service applies client-side controls (blocked right-click, blocked Ctrl+P, blur on tab focus
              loss) as a courtesy to protect your work-in-progress from casual capture. These controls are not
              a security guarantee and can be bypassed by a determined user. You should not rely on them as a
              substitute for handling sensitive information carefully.
            </p>
          </Section>

          <Section n="10" title="Availability &amp; Changes">
            <p>
              The Service is provided on an "as available" basis. We may modify, suspend or discontinue any
              feature at any time without notice. We may also update these Terms; continued use after an
              update constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section n="11" title="Disclaimer of Warranties">
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR
              IMPLIED, INCLUDING WITHOUT LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
              PURPOSE, ACCURACY, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE
              UNINTERRUPTED, ERROR-FREE, OR THAT AI-GENERATED CONTENT WILL MEET YOUR REQUIREMENTS.
            </p>
          </Section>

          <Section n="12" title="Limitation of Liability">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MYRESUMEDESIGN AND ITS OPERATORS SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL OR EXEMPLARY DAMAGES, OR FOR ANY LOSS OF DATA,
              LOST OPPORTUNITIES, LOST OFFERS, OR LOST INCOME ARISING OUT OF OR RELATED TO YOUR USE OF THE
              SERVICE. OUR TOTAL AGGREGATE LIABILITY FOR ANY CLAIM SHALL NOT EXCEED THE AMOUNT (IF ANY) YOU
              PAID US FOR THE SPECIFIC DOWNLOAD GIVING RISE TO THE CLAIM.
            </p>
          </Section>

          <Section n="13" title="Indemnity">
            <p>
              You agree to indemnify and hold MyResumeDesign harmless from any claim, demand, loss, or damage
              arising out of your content, your use of the Service, or your violation of these Terms.
            </p>
          </Section>

          <Section n="14" title="Governing Law &amp; Jurisdiction">
            <p>
              These Terms are governed by and construed in accordance with the laws of the Republic of India,
              without regard to conflict-of-law principles. Any dispute, claim or controversy arising out of
              or relating to these Terms or the use of the Service shall be subject to the <b>exclusive
              jurisdiction of the competent courts located in Hyderabad, Telangana, India</b>. By using the
              Service, you consent to the personal jurisdiction and venue of those courts.
            </p>
          </Section>

          <Section n="15" title="Contact">
            <p>
              Questions about these Terms may be directed to
              {" "}<a href="mailto:contact@myresumedesign.com" className="underline hover:text-black">contact@myresumedesign.com</a>.
            </p>
          </Section>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-black/10 py-8">
        <div className="max-w-[900px] mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-neutral-500">
          <div className="font-mono">© 2026 MyResumeDesign</div>
          <div className="flex gap-6 font-mono uppercase tracking-widest text-xs">
            <Link to="/help" className="hover:text-black">Help</Link>
            <Link to="/privacy" className="hover:text-black">Privacy</Link>
            <Link to="/terms" className="hover:text-black">Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section>
      <div className="flex items-baseline gap-3 mb-3">
        <span className="font-mono text-xs text-neutral-400">§ {n}</span>
        <h2 className="font-heading font-bold text-xl tracking-tight">{title}</h2>
      </div>
      <div className="space-y-3 text-sm">{children}</div>
    </section>
  );
}
